import { useContext, useEffect, useMemo, useState } from 'preact/hooks';
import type { BuiltinAnswers, CustomAnswer, DocumentCompletion, PdfDocumentDefinition, PublicEvent } from '../../shared/types';
import type { PdfEngineIssue } from '../../shared/pdf/errors';
import { resolvePdfBindings } from '../../shared/pdfBindings';
import { loadOrganizerPdf, loadParticipantPdf, pdfActionError, type OrganizerPdfSource } from '../lib/pdfForms';
import type { FillOptions } from '../lib/pdf';
import { ApiError } from '../lib/api';
import { ArtifactsContext, artifactKey, bytesKey, defaultArtifacts, type PreparedArtifact, type RegistrationArtifacts } from './artifacts';

export { signaturePayload, signatureAppearance, completionSignatures } from './pdfSignatures';
import { completionSignatures } from './pdfSignatures';
import { reportClientError } from '../lib/clientErrors';

export function useArtifacts(owner?: RegistrationArtifacts): RegistrationArtifacts {
  const context = useContext(ArtifactsContext), store = owner ?? context ?? defaultArtifacts();
  const [, setVersion] = useState(0);
  useEffect(() => store.subscribe(() => setVersion(v => v + 1)), [store]);
  return store;
}

/** Slot, source and artifact identity for one document. Freezing uses this to prove an artifact is current. */
/** `submittedOn` is the registration's one date, the same on every PDF it sends: the standard form's signing day. */
export function documentArtifactRequest(event: PublicEvent, document: PdfDocumentDefinition, builtin: BuiltinAnswers, custom: Record<string, CustomAnswer>, completion: DocumentCompletion, preview: boolean, submittedOn: string) {
  const sourceKey = JSON.stringify([event.eventId, event.publishedRevisionId, document.id, document.assetId, document.templateSha256, preview]);
  const context = { event, document, builtin, custom, documentAnswers: completion.answers, signatures: completionSignatures(completion), submittedOn };
  const resolved = resolvePdfBindings(context, { requireComplete: true });
  const mode = resolved.issues.length ? 'preview' as const : 'final' as const;
  return { slot: `document:${document.id}`, sourceKey, context, mode, key: artifactKey([sourceKey, document, resolved.values, mode]) };
}
export const standardArtifactKey = (options: FillOptions) => artifactKey([bytesKey(options.template), { ...options, template: undefined }]);

/**
 * The current prepared PDF for one additional document. Generation runs in the preparation worker;
 * the key covers only the values the document references, so a review confirmation alone does not
 * regenerate. Once every required value and signature is present the artifact is prepared in final
 * mode, and those are the bytes shown, downloaded and delivered.
 */
export function useParticipantPdf(event: PublicEvent, document: PdfDocumentDefinition, builtin: BuiltinAnswers, custom: Record<string, CustomAnswer>, completion: DocumentCompletion, preview: boolean, submittedOn: string) {
  const store = useArtifacts();
  const [retries, setRetries] = useState(0);
  const { slot, sourceKey, key, mode, context } = useMemo(() => documentArtifactRequest(event, document, builtin, custom, completion, preview, submittedOn), [event, document, builtin, custom, completion, preview, submittedOn]);
  const [source, setSource] = useState<{ key: string; value: OrganizerPdfSource }>();
  const [sourceError, setSourceError] = useState<{ key: string; message: string }>();
  useEffect(() => {
    let live = true;
    setSource(undefined); setSourceError(undefined);
    store.source(sourceKey, () => {
      const signal = new AbortController().signal;
      return preview && event.eventId ? loadOrganizerPdf(event.eventId, document, signal) : loadParticipantPdf(event, document, signal);
    }).then(value => { if (live) setSource({ key: sourceKey, value }); }).catch(error => {
      reportClientError('register: load form source', error, { eventId: event.eventId, documentId: document.id, preview });
      if (live) setSourceError({ key: sourceKey, message: preview ? pdfActionError(error) : error instanceof ApiError && error.code === 'revision_conflict' ? 'The event changed. Reload this page to continue; your answers are kept.' : 'Could not load this document. Retry, reload this page, or contact the organizer.' });
    });
    return () => { live = false; };
  }, [sourceKey, retries]);

  useEffect(() => {
    if (source?.key !== sourceKey) return;
    // Debounce typing; superseded queued work never starts and stale results are dropped.
    const timer = setTimeout(() => store.request(slot, key, mode, async () => ({ kind: 'document', input: { ...context, template: source.value.bytes, mode } })), 200);
    return () => clearTimeout(timer);
  }, [key, source, retries]);

  const state = store.get(slot), current = state?.key === key ? state : undefined;
  const failedSource = sourceError?.key === sourceKey;
  const issues: PdfEngineIssue[] = failedSource ? [{ code: 'template_unavailable', documentId: document.id, message: sourceError!.message }] : current?.status === 'failed' ? current.issues : [];
  return {
    generated: current?.status === 'ready' ? current.artifact : undefined,
    issues, preparing: !failedSource && current?.status !== 'ready' && current?.status !== 'failed',
    source: source?.key === sourceKey ? source.value : undefined,
    retry: () => { store.retry(slot, failedSource ? sourceKey : undefined); setRetries(r => r + 1); }, key,
  };
}

/** The standard permission form for this registration, prepared in the worker from `options`. */
export function useStandardPdf(slot: 'standard' | 'standard-preview', options: FillOptions | null, owner?: RegistrationArtifacts): { artifact?: PreparedArtifact; preparing: boolean; issues: PdfEngineIssue[]; retry: () => void } {
  const store = useArtifacts(owner);
  const [retries, setRetries] = useState(0);
  const key = options ? standardArtifactKey(options) : undefined;
  useEffect(() => {
    if (!options || !key) return;
    const mode = options.participantSignature ? 'final' : 'preview';
    store.request(slot, key, mode, async () => ({ kind: 'standard', options }));
  }, [key, retries]);
  const state = store.get(slot), current = key && state?.key === key ? state : undefined;
  return {
    artifact: current?.status === 'ready' ? current.artifact : undefined,
    preparing: !!key && current?.status !== 'ready' && current?.status !== 'failed',
    issues: current?.status === 'failed' ? current.issues : [],
    retry: () => { store.retry(slot); setRetries(r => r + 1); },
  };
}
